/**
 * Compare Page Utilities
 *
 * Constants, types, and helper functions for fund comparison.
 */

import type { Fund, FundRating, FundCategory } from "@curie/shared";

// ============================================================================
// Constants
// ============================================================================

/** Empty array constant to prevent re-renders */
export const EMPTY_ISINS: string[] = [];

/**
 * Category display labels
 */
export const CATEGORY_LABELS: Record<FundCategory, string> = {
  "equity-large-cap": "Large Cap",
  "equity-mid-cap": "Mid Cap",
  "equity-small-cap": "Small Cap",
  "equity-flexi-cap": "Flexi Cap",
  "equity-multi-cap": "Multi Cap",
  "equity-elss": "ELSS",
  "equity-sectoral": "Sectoral",
  "equity-global": "Global",
  "debt-liquid": "Liquid",
  "debt-short-term": "Short Term",
  "debt-medium-term": "Medium Term",
  "debt-long-term": "Long Term",
  "debt-gilt": "Gilt",
  "debt-corporate": "Corporate",
  "hybrid-aggressive": "Aggressive Hybrid",
  "hybrid-balanced": "Balanced Hybrid",
  "hybrid-conservative": "Conservative Hybrid",
};

/**
 * Rating display configuration
 */
export const RATING_CONFIG: Record<
  FundRating,
  { label: string; color: string; stars: number }
> = {
  "in-form": { label: "In Form", color: "text-green-600", stars: 5 },
  "on-track": { label: "On Track", color: "text-blue-600", stars: 4 },
  "off-track": { label: "Off Track", color: "text-yellow-600", stars: 3 },
  "out-of-form": { label: "Out of Form", color: "text-red-600", stars: 2 },
};

// ============================================================================
// Types
// ============================================================================

export interface ComparisonMetric {
  label: string;
  key: keyof Fund | "curieScore";
  format: (value: number) => string;
  lowerIsBetter: boolean;
  getValue?: (fund: Fund) => number;
}

export interface BestValues {
  rank: number;
  curieScore: number;
  consistencyScore: number;
  recencyScore: number;
  volatilityScore: number;
  expenseRatio: number;
  returns1Y: number;
  returns3Y: number;
  returns5Y: number;
  aum: number;
  sharpeRatio: number;
  sortinoRatio: number;
  alpha: number;
  beta: number;
  minSipAmount: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate Curie Score from fund components
 */
export function calculateCurieScore(fund: Fund): number {
  return Math.round(
    (fund.consistencyScore + fund.recencyScore + fund.volatilityScore) / 3
  );
}

/**
 * Format AUM for display
 */
export function formatAum(aum: number): string {
  if (aum >= 10000) {
    return `${(aum / 10000).toFixed(1)}L Cr`;
  }
  if (aum >= 100) {
    return `${(aum / 100).toFixed(0)} K Cr`;
  }
  return `${aum.toFixed(0)} Cr`;
}

/**
 * Calculate best values for all metrics across funds
 * Pre-computed once to avoid O(n²) calculations
 */
export function calculateBestValues(funds: Fund[]): BestValues {
  if (funds.length === 0) {
    return {
      rank: 0,
      curieScore: 0,
      consistencyScore: 0,
      recencyScore: 0,
      volatilityScore: 0,
      expenseRatio: 0,
      returns1Y: 0,
      returns3Y: 0,
      returns5Y: 0,
      aum: 0,
      sharpeRatio: 0,
      sortinoRatio: 0,
      alpha: 0,
      beta: 0,
      minSipAmount: 0,
    };
  }

  return {
    // Lower is better
    rank: Math.min(...funds.map((f) => f.rank)),
    expenseRatio: Math.min(...funds.map((f) => f.expenseRatio)),
    minSipAmount: Math.min(...funds.map((f) => f.minSipAmount)),
    // Higher is better
    curieScore: Math.max(...funds.map(calculateCurieScore)),
    consistencyScore: Math.max(...funds.map((f) => f.consistencyScore)),
    recencyScore: Math.max(...funds.map((f) => f.recencyScore)),
    volatilityScore: Math.max(...funds.map((f) => f.volatilityScore)),
    returns1Y: Math.max(...funds.map((f) => f.returns1Y)),
    returns3Y: Math.max(...funds.map((f) => f.returns3Y)),
    returns5Y: Math.max(...funds.map((f) => f.returns5Y)),
    aum: Math.max(...funds.map((f) => f.aum)),
    sharpeRatio: Math.max(...funds.map((f) => f.sharpeRatio)),
    sortinoRatio: Math.max(...funds.map((f) => f.sortinoRatio)),
    alpha: Math.max(...funds.map((f) => f.alpha)),
    beta: Math.max(...funds.map((f) => f.beta)),
  };
}

/**
 * Get score color based on value
 */
export function getScoreColor(score: number): string {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-blue-600";
  if (score >= 40) return "text-yellow-600";
  return "text-red-600";
}

// ============================================================================
// Metric Definitions (Static - no need for useMemo)
// ============================================================================

export const COMPARISON_METRICS: ComparisonMetric[] = [
  {
    label: "Expense Ratio",
    key: "expenseRatio",
    format: (v) => `${v.toFixed(2)}%`,
    lowerIsBetter: true,
  },
  {
    label: "1-Year Return",
    key: "returns1Y",
    format: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`,
    lowerIsBetter: false,
  },
  {
    label: "3-Year Return",
    key: "returns3Y",
    format: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`,
    lowerIsBetter: false,
  },
  {
    label: "5-Year Return",
    key: "returns5Y",
    format: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`,
    lowerIsBetter: false,
  },
  {
    label: "AUM",
    key: "aum",
    format: (v) => formatAum(v),
    lowerIsBetter: false,
  },
  {
    label: "Sharpe Ratio",
    key: "sharpeRatio",
    format: (v) => v.toFixed(2),
    lowerIsBetter: false,
  },
  {
    label: "Sortino Ratio",
    key: "sortinoRatio",
    format: (v) => v.toFixed(2),
    lowerIsBetter: false,
  },
  {
    label: "Alpha",
    key: "alpha",
    format: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}`,
    lowerIsBetter: false,
  },
  {
    label: "Beta",
    key: "beta",
    format: (v) => v.toFixed(2),
    lowerIsBetter: false,
  },
  {
    label: "Min SIP Amount",
    key: "minSipAmount",
    format: (v) => `₹${v.toLocaleString("en-IN")}`,
    lowerIsBetter: true,
  },
];
